import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { PermissionScope } from '../../api/interfaces/permissions';
import type { UserBranchSource } from '../../api/interfaces/branches';
import { clearAuthSessionStorage } from '../utils/session-storage';
export interface UserPermissionGrant {
  readonly name: string;
  readonly scopes: PermissionScope[];
}

export interface User {
  readonly id: string;
  readonly organisationType?: string | null;
  readonly branchSource?: UserBranchSource;
  readonly canAccessCaseAttachments?: boolean;
  readonly canUseSupportAssistant?: boolean;
  readonly email?: string;
  readonly name: string;
  readonly roleName?: string | null;
  readonly branches?: {
    readonly id?: string;
    readonly name?: string;
  }[];
  readonly permissions: UserPermissionGrant[];
  readonly isImpersonating?: boolean;
}

export type AuthStore = {
  readonly hydrated: boolean;
  readonly setHydrated: (hydrated: boolean) => void;

  readonly user: User | null;
  readonly setUser: (user: User | null) => void;

  readonly authErrorMessage: string | null;
  readonly setAuthErrorMessage: (message: string | null) => void;

  readonly sessionExpired: boolean;
  readonly setSessionExpired: (sessionExpired: boolean) => void;

  readonly logout: () => void;
};

const areStringArraysEqual = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areBranchesEqual = (left: User['branches'], right: User['branches']) => {
  if (left === right) {
    return true;
  }

  if (!left || !right || left.length !== right.length) {
    return false;
  }

  return left.every((branch, index) => {
    const other = right[index];

    return other !== undefined && branch.id === other.id && branch.name === other.name;
  });
};

const arePermissionGrantsEqual = (
  left: readonly UserPermissionGrant[],
  right: readonly UserPermissionGrant[],
) => {
  if (left === right) {
    return true;
  }

  if (left.length !== right.length) {
    return false;
  }

  return left.every((permission, index) => {
    const other = right[index];

    return (
      other !== undefined &&
      permission.name === other.name &&
      areStringArraysEqual(permission.scopes, other.scopes)
    );
  });
};

const areUsersEqual = (left: User | null, right: User | null) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.id === right.id &&
    left.email === right.email &&
    left.name === right.name &&
    left.roleName === right.roleName &&
    left.organisationType === right.organisationType &&
    left.branchSource === right.branchSource &&
    left.canAccessCaseAttachments === right.canAccessCaseAttachments &&
    left.canUseSupportAssistant === right.canUseSupportAssistant &&
    left.isImpersonating === right.isImpersonating &&
    areBranchesEqual(left.branches, right.branches) &&
    arePermissionGrantsEqual(left.permissions, right.permissions)
  );
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      hydrated: false,
      setHydrated: (hydrated) => set({ hydrated }),

      user: null,
      setUser: (user) =>
        set((state) => {
          // A reload starts with user=null until the server confirms the session.
          if (state.user && (state.user.id !== user?.id
            || Boolean(state.user.isImpersonating) !== Boolean(user?.isImpersonating))) {
            clearAuthSessionStorage();
          }
          return areUsersEqual(state.user, user) ? state : { user };
        }),

      authErrorMessage: null,
      setAuthErrorMessage: (authErrorMessage) => set({ authErrorMessage }),

      sessionExpired: false,
      setSessionExpired: (sessionExpired) => set({ sessionExpired }),

      logout: () => {
        clearAuthSessionStorage();
        set({ user: null });
      },
    }),
    {
      name: 'jobtracker.auth',
      // Session cookies authenticate requests; office scope and assistant access must be fetched again.
      partialize: () => ({}),
      merge: (_persisted, current) => current,
      migrate: () => ({}),
      onRehydrateStorage: () => (state) => state?.setHydrated(true),
      version: 2,
    },
  ),
);
